/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { memo } from 'react';

import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Tag, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import type { ArticleCardProps as Props } from 'bundles/shared-articles/types';

import _t from 'i18n!nls/shared-articles';

const styles = {
  clamp: css`
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
  `,

  root: () => css`
    border-radius: var(--cds-spacing-100);
    background-color: var(--cds-color-white-0);
    box-shadow: 0 2px 10px rgb(0 0 0 / 10%);
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
    min-height: inherit;
    overflow: hidden;
    padding: var(--cds-spacing-150);
    position: relative;
    width: 100%;
    height: inherit;

    ${breakpoints.up('sm')} {
      gap: var(--cds-spacing-300);
      min-height: inherit;
      padding: var(--cds-spacing-150) var(--cds-spacing-200);
    }
  `,

  image: () => css`
    height: 110px;
    margin: calc(var(--cds-spacing-150) * -1) calc(var(--cds-spacing-150) * -1) 0 calc(var(--cds-spacing-150) * -1);
    object-fit: cover;
    width: calc(100% + var(--cds-spacing-150) * 2);

    ${breakpoints.up('sm')} {
      height: 215px;
      margin: calc(var(--cds-spacing-150) * -1) calc(var(--cds-spacing-200) * -1) 0 calc(var(--cds-spacing-200) * -1);
      width: calc(100% + var(--cds-spacing-200) * 2);
    }
  `,

  content: () => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: var(--cds-spacing-100);
  `,

  metadata: () => css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,

  subtitle: () => css`
    display: none;

    ${breakpoints.up('sm')} {
      display: -webkit-box;
    }
  `,

  title: css`
    font-size: 16px;
    line-height: 20px;
    margin-top: 5px;
  `,
  featuredTagContainer: css`
    position: absolute;
    right: var(--cds-spacing-100);
    top: var(--cds-spacing-100);
    z-index: 1;

    // Override default CDS tag styles to keep it consistent with the styles on search page product cards
    // See bundles/product-card/components/ProductCardV2.tsx
    .cds-tag-status {
      border: 1px solid var(--cds-color-neutral-stroke-primary) !important;

      > span {
        color: var(--cds-color-neutral-primary);
      }
    }
  `,
};

export const ArticleCard = ({
  id,
  title,
  subtitle,
  image,
  imageLazyLoad,
  isFeatured,
  metadata: { estimatedReadingTime, publishedDate },
  alwaysShowSubtitle,
}: Props) => (
  <article id={id} css={styles.root}>
    {image && (
      <img alt={image.description} css={styles.image} src={image.url} loading={imageLazyLoad ? 'lazy' : undefined} />
    )}
    {isFeatured && (
      <div css={styles.featuredTagContainer}>
        <Tag variant="status" priority="secondary">
          {_t('Featured')}
        </Tag>
      </div>
    )}
    <div css={styles.content}>
      <Typography css={[styles.clamp, styles.title]} variant="h2semibold">
        {title}
      </Typography>
      {subtitle && (
        <Typography2
          variant="bodySecondary"
          color="supportText"
          css={alwaysShowSubtitle ? styles.clamp : [styles.clamp, styles.subtitle]}
          component="p"
        >
          {subtitle}
        </Typography2>
      )}
    </div>
    <div css={styles.metadata}>
      {publishedDate && (
        <Typography2 variant="bodySecondary" color="supportText" component="p">
          {formatDateTimeDisplay(publishedDate, LONG_DATE_ONLY_DISPLAY)}
        </Typography2>
      )}
      <Typography2 variant="bodySecondary" color="supportText" component="p">
        {_t('Article')}
        {estimatedReadingTime && ` · ${estimatedReadingTime}`}
      </Typography2>
    </div>
  </article>
);

export default memo(ArticleCard);
